import {Fragment, useCallback, useContext, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {videoTokenApi} from 'api';
import {Loader, Button, SessionWebRTCRoom} from 'components';
import {ClientContext} from 'context';
import {useClientSession} from 'hooks';
import {TRANSLATION_KEYS} from 'locale/translation-keys';

import {ClientView} from '../types';

const PAGE_KEYS = TRANSLATION_KEYS.CLIENT_IN_SESSION_PAGE;
const clientScreenshotId = 'video-screenshot-client-selfie';

export const ClientConferenceVideoOnlyContent: React.FC = () => {
    const {t} = useTranslation();
    const {participantId, audioVideoActivity, webRTCRoomName, participantIds, audioVideoView, audioVideoAsColumn} = useClientSession();
    const {
        currentView, isDevelopmentMode, videoDevices, audioEnabled, videoEnabled, videoConstraints, videoPinParticipant,
        endLocalSession, setMediaDevices, notifyWebRTCStartSuccess, notifyWebRTCStartFailed, setTracks
    } = useContext(ClientContext);

    const mediaConstraints = useMemo(() => ({
        audioinput: Boolean(audioVideoActivity),
        ...(Boolean(audioVideoActivity) && {videoinput: videoConstraints})
    }), [audioVideoActivity, videoConstraints]);

    const renderEndedView = useCallback(() => {
        if (isDevelopmentMode && (!currentView || currentView === ClientView.SessionEnded)) {
            return (
                <div className='tw-text-center tw-overflow-hidden'>
                    <h1>{t(PAGE_KEYS.SESSION_NOT_ACTIVE)}</h1>
                    <h2>{t(PAGE_KEYS.CLICK_START_INIT_CALL)}</h2>
                </div>
            );
        }
        return null;
    }, [currentView, isDevelopmentMode, t]);

    const renderCallingView = useCallback(() => {
        if (isDevelopmentMode && currentView === ClientView.Calling) {
            return (
                <div className='tw-text-center tw-overflow-hidden'>
                    <h2>{t(PAGE_KEYS.CALLING_TO_AGENT)}</h2>
                    <Loader svgClassName='tw-max-w-12 tw-max-h-12' />

                    <Button
                        className='tw-m-2'
                        color='primary'
                        onClick={endLocalSession}
                    >
                        {t(PAGE_KEYS.CANCEL_BTN)}
                    </Button>
                </div>
            );
        }
        return null;
    }, [currentView, endLocalSession, isDevelopmentMode, t]);

    const renderInSessionView = useCallback(() => {
        if (currentView === ClientView.InSession) {
            return (
                <div className='tw-w-screen tw-h-screen tw-overflow-auto tw-bg-layout'>
                    {webRTCRoomName && audioVideoActivity &&
                        <div className='tw-flex-center tw-h-full tw-w-full'>
                            <SessionWebRTCRoom
                                participantId={participantId}
                                participantIds={participantIds}
                                videoPinParticipant={videoPinParticipant}
                                roomName={webRTCRoomName}
                                ignoreErrorDialog
                                audioVideoActivity={audioVideoActivity}
                                audioEnabled={audioEnabled}
                                videoEnabled={videoEnabled}
                                videoDevices={videoDevices}
                                mediaConstraints={mediaConstraints}
                                defaultView={audioVideoView}
                                audioVideoAsColumn={audioVideoAsColumn}
                                getTwilioTokenFn={videoTokenApi.getClientTwilioVideoToken}
                                getKurentoTokenFn={videoTokenApi.getClientInternalWebRTCToken}
                                notifyWebRTCStartSuccess={notifyWebRTCStartSuccess}
                                notifyWebRTCStartFailed={notifyWebRTCStartFailed}
                                onEnumerateMediaDevices={setMediaDevices}
                                onEnumerateTracks={setTracks}
                            />
                        </div>}
                    <canvas id={clientScreenshotId} className='tw-hidden' />
                </div>
            );
        }
        return null;
    }, [
        currentView, webRTCRoomName, audioVideoActivity, participantId, audioEnabled, audioVideoView,
        videoEnabled, videoDevices, mediaConstraints, videoPinParticipant, participantIds, audioVideoAsColumn,
        notifyWebRTCStartSuccess, notifyWebRTCStartFailed, setMediaDevices, setTracks
    ]);

    return (
        <Fragment>
            {renderCallingView()}
            {renderInSessionView()}
            {renderEndedView()}
        </Fragment>
    );
};
